import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "@/portal/api";
import { resolveBrand, brandStyle, brandLogoSrc, EFFECTDEAL_BRAND } from "@/flotila/brand";
import "@/flotila/flotila.css";
import { Loader2, LogIn, Car, Camera, Gauge, ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/portal/AuthContext";
import { LOGO_IMG } from "@/portal/components/adminShared";

const FEATURES = [
  { icon: Gauge, title: "Nájezd každý měsíc", text: "Přesný stav tachometru a rozdíl km oproti minulému měsíci." },
  { icon: Camera, title: "Fotodokumentace", text: "Fotky vozu z každého měsíce na jednom místě." },
  { icon: ShieldCheck, title: "Servis, STK, pojistka", text: "Termíny s barevným upozorněním, nic nepropadne." },
];

const FlotilaLoginPage = () => {
  const { login, user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [brand, setBrand] = useState(EFFECTDEAL_BRAND);
  useEffect(() => {
    if (!slug) { setBrand(EFFECTDEAL_BRAND); return; }
    apiFetch(`/flotila/brand/${slug}`).then((b) => setBrand(resolveBrand(b))).catch(() => setBrand(EFFECTDEAL_BRAND));
  }, [slug]);
  const logo = brandLogoSrc(brand);
  const Brand = () => (
    <div className="flex items-center gap-3">
      {logo ? <img src={logo} alt={brand.company_name} className="h-10 max-w-[170px] object-contain" /> : <div className="w-10 h-10 rounded-xl fl-accent-bg flex items-center justify-center font-bold">{(brand.company_name || "F").slice(0, 1).toUpperCase()}</div>}
      <span className="font-['Cabinet_Grotesk'] font-bold tracking-wide">{brand.is_default ? <>EFFECTDEAL <span className="fl-accent-text">FLOTILA</span></> : <>{brand.company_name} <span className="fl-accent-text">FLOTILA</span></>}</span>
    </div>
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const canUseFleet = (r) => r === "admin" || r === "assistant" || r === "partner";
  useEffect(() => {
    if (!loading && user && canUseFleet(user.role)) navigate("/flotila", { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const u = await login(email, password);
      if (!canUseFleet(u.role)) { logout(); setError("Tento účet nemá přístup do Flotily. Klientskou zónu najdete na stránce Přihlášení."); return; }
      navigate("/flotila", { replace: true });
    } catch (err) {
      setError(err.message || "Přihlášení se nezdařilo");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen fl-root grid lg:grid-cols-2" data-fl-theme={brand.theme} style={brandStyle(brand)} data-testid="flotila-login-page" data-brand={brand.slug || "effectdeal"}>
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r fl-border">
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full fl-glow blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[360px] h-[360px] rounded-full fl-glow blur-3xl opacity-60" />
        <div className="relative"><Brand /></div>
        <div className="relative max-w-md">
          <p className="text-xs uppercase tracking-[0.2em] fl-accent-text mb-3">{brand.is_default ? "Partnerský portál" : brand.tagline || "Přehled vozů"}</p>
          <h1 className="font-['Cabinet_Grotesk'] text-4xl xl:text-5xl font-bold leading-tight mb-4">Vaše vozy pod kontrolou. Každý měsíc.</h1>
          <p className="fl-muted mb-10">{brand.is_default ? "Přehled vozů, které má EffectDeal ve správě" : `Přehled vozů společnosti ${brand.company_name} ve správě EffectDeal`} – stav tachometru, fotodokumentace a servisní termíny. Jen pro čtení, vždy aktuální.</p>
          <div className="space-y-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-4">
                <div className="w-10 h-10 rounded-xl fl-accent-soft flex items-center justify-center shrink-0"><f.icon className="w-5 h-5" /></div>
                <div><p className="font-semibold">{f.title}</p><p className="text-sm fl-muted">{f.text}</p></div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs fl-muted inline-flex items-center gap-1.5">{brand.is_default ? "© EffectDeal · IČO 24952923" : <>powered by <img src={LOGO_IMG} alt="" className="w-4 h-4 object-contain" /> EffectDeal</>} · marketing@effectdeal.com</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex lg:hidden items-center gap-3 mb-8"><Brand /></div>
          <div className="fl-card rounded-3xl p-6 sm:p-8">
            <div className="w-12 h-12 rounded-2xl fl-accent-soft flex items-center justify-center mb-5"><Car className="w-6 h-6" /></div>
            <h2 className="font-['Cabinet_Grotesk'] text-2xl font-bold mb-1">{brand.is_default ? "Přihlášení do Flotily" : `Přihlášení · ${brand.company_name}`}</h2>
            <p className="text-sm fl-muted mb-6">{brand.is_default ? "Pro partnery a tým EffectDeal. " : ""}Přístupové údaje jste obdrželi e-mailem.</p>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="flotila-login-form">
              <div>
                <label className="text-xs uppercase tracking-wider fl-muted block mb-1.5">E-mail</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className="fl-input w-full" placeholder="vas@email.cz" data-testid="flotila-login-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider fl-muted block mb-1.5">Heslo</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="fl-input w-full" placeholder="••••••••" data-testid="flotila-login-password" />
              </div>
              {error && <p className="text-sm fl-bad bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2" data-testid="flotila-login-error">{error}</p>}
              <button type="submit" disabled={submitting} className="fl-btn w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="flotila-login-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Přihlásit se
              </button>
            </form>
            <p className="text-xs fl-muted mt-5">Nemáte přístup nebo jste zapomněli heslo? Napište na <a href="mailto:marketing@effectdeal.com" className="fl-accent-text hover:fl-accent-text">marketing@effectdeal.com</a> a pošleme vám nový odkaz.</p>
          </div>
          <a href={brand.is_default ? "/" : brand.website || "/"} className="mt-6 inline-flex items-center gap-1.5 text-sm fl-muted hover:opacity-80" data-testid="flotila-login-home"><ArrowLeft className="w-4 h-4" /> {brand.is_default ? "Zpět na effectdeal.com" : `Zpět na ${(brand.website || "effectdeal.com").replace(/^https?:\/\//, "")}`}</a>
        </div>
      </div>
    </div>
  );
};

export default FlotilaLoginPage;
